import { memo, useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import "./TravellersRoomButton.scss";
import classNames from "classnames";
import TravellersRoomForm from "app/pages/SmartDP/Search/TravellersRoomInput/TravellersRoomForm";
import {
	autoUpdate,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useDismiss,
	useFloating,
	useFocus,
	useInteractions,
} from "@floating-ui/react";
import { useFormikContext } from "formik";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import { FormattedMessage, FormattedPlural } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import IconSearchFormDropdown from "app/pages/.shared/static/icons/IconSearchFormDropdown";

const TravellersRoomButton = ({
	className,
	initialValues = [],
	onConfirmation = () => {},
	onFocus = () => {},
	id,
	loading,
	stickyMode = false,
	validateOnMount,
	warningMessage = false,
	popperOffset = [],
	isAutoScrollActive,
	popperWidth,
}) => {
	const [open, setOpen] = useState(false);

	const { errors } = useFormikContext();

	const floatingElementRef = useRef(null);

	const inputClassName = classNames(
		"travellers-room-button",
		"travellers-room-button--touched",
		className,
		{
			"travellers-room-button--error": errors?.occupancies,
			"travellers-room-button--opened": open,
		}
	);

	const travelerCount = initialValues.reduce((acc, occupancy) => {
		return acc + occupancy.adults + occupancy.children;
	}, 0);

	const roomCount = initialValues.length;

	const handleOnOpen = useCallback(shouldOpen => {
		setOpen(shouldOpen);
		if (shouldOpen) {
			onFocus();
		}
	}, []);

	const { context, x, y, strategy, refs } = useFloating({
		placement: "bottom",
		whileElementsMounted: autoUpdate,
		open,
		onOpenChange: handleOnOpen,
		middleware: [offset(), shift()],
	});

	const handleValidation = useCallback(
		values => {
			setOpen(false);

			/* FQ-338
			// relaunch validation to remove error color if there is no errors
			validateForm({
				...formValues,
				...values,
			});
			*/

			onConfirmation(values.occupancies);
		},
		[refs?.reference?.current]
	);

	const dismiss = useDismiss(context);
	const click = useClick(context);
	// gere le click et le focus par clavier
	const focus = useFocus(context, {
		keyboardOnly: false,
	});

	const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click, focus]);

	useEffect(() => {
		const handleScroll = () => {
			// do not close dropdown if errors from emirates redirect
			const shouldClosePopover = !errors?.occupancies || errors?.occupancies?.length === 0;
			if (floatingElementRef?.current && !isAutoScrollActive) {
				const { top, height } = floatingElementRef.current.getBoundingClientRect();
				const windowHeight = window.innerHeight;

				const calendarMidpoint = top + height / 2;

				const isHalfCalendarVisible =
					calendarMidpoint > 0 && calendarMidpoint < windowHeight;

				if (!isHalfCalendarVisible && open && shouldClosePopover) {
					setOpen(false);
				}
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [open, errors?.occupancies, floatingElementRef?.current, isAutoScrollActive]);

	return (
		<>
			<div
				className={inputClassName}
				ref={refs.setReference}
				id={id}
				tabIndex="0"
				{...getReferenceProps({})}
			>
				{!loading ? (
					<>
						<div
							className="travellers-room-button__input"
							data-testid="travellers-room-button"
						>
							<Typography component="p" variant={TYPOGRAPHY_VARIANTS.REGULAR}>
								<FormattedMessage
									id="traveler.count"
									values={{ count: travelerCount }}
								/>
								,{" "}
								<FormattedPlural
									value={roomCount}
									one={
										<FormattedMessage
											values={{ count: roomCount }}
											id="general.room.count.label.singular"
										/>
									}
									other={
										<FormattedMessage
											values={{ count: roomCount }}
											id="general.room.count.label.plural"
										/>
									}
								/>
							</Typography>

							<IconSearchFormDropdown width={16} height={16} />
						</div>
					</>
				) : (
					<div className="travellers-room-button__loader">
						<LoaderBar height={10} width={"80%"} />
						<LoaderBar height={10} />
					</div>
				)}
			</div>
			{!loading && (
				<FloatingPortal preserveTabOrder={true}>
					{open && (
						<div
							ref={node => {
								refs.setFloating(node);
								floatingElementRef.current = node;
							}}
							className={classNames({
								"travellers-room-button__popover": true,
								"travellers-room-button__popover--sticky": stickyMode,
							})}
							{...getFloatingProps({
								style: {
									position: strategy,
									left: x + popperOffset[0] ?? "",
									top: y + popperOffset[1] ?? "",
									width: popperWidth,
								},
							})}
						>
							<div className="travellers-room-button__popover-body">
								<TravellersRoomForm
									initialValues={initialValues}
									handleValidation={handleValidation}
									validateOnMount={validateOnMount}
									warningMessage={warningMessage}
								/>
							</div>
						</div>
					)}
				</FloatingPortal>
			)}
		</>
	);
};

TravellersRoomButton.propTypes = {
	className: PropTypes.string,
	onConfirmation: PropTypes.func,
	onFocus: PropTypes.func,
	initialValues: PropTypes.array,
	id: PropTypes.string,
	stickyMode: PropTypes.bool,
	validateOnMount: PropTypes.bool,
	loading: PropTypes.bool,
	popperOffset: PropTypes.array,
	popperWidth: PropTypes.number,
	isAutoScrollActive: PropTypes.bool,
	warningMessage: PropTypes.bool,
};

export default memo(TravellersRoomButton);
